/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import { Person } from '../person'
import { SectionTitle } from '../sectionTitle'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { nest } from 'd3-collection'

// TODO Set the correct article ID here
const ARTICLE_ID = '766870256747'

const mediaName = parseHostname().publicationName
// console.log('mediaName:', mediaName)

export class AppComponent extends React.Component<Props, State> {
  constructor(props) {
    super(props)
    this.state = {
      tenantText: {},
      peopleData: {}
    }
  }

  extractPublicationDate(rawData) {
    // extract publication date
    const months = [
      'Januar',
      'Februar',
      'März',
      'April',
      'Mai',
      'Juni',
      'Juli',
      'August',
      'September',
      'Oktober',
      'November',
      'Dezember'
    ]
    const dateObject = new Date(rawData.updated)
    const d = dateObject.getDate()
    const m = dateObject.getMonth()
    const y = dateObject.getFullYear()
    return d + '. ' + months[m] + ' ' + y
  }

  componentDidMount() {
    // console.log('componentDidMount')
    /* GET SHEET DATA VIA CLOUD STORAGE */
    const textURL =
      'https://storage.googleapis.com/ta-interaktiv/mitarbeiter-der-redaktion/2022-texte.json'
    // console.log('dataURL:', dataURL)
    fetch(textURL)
      .then(response => {
        if (response.ok) {
          return response.json()
        } else {
          throw new Error('JSON Data was not OK')
        }
      })
      .then(responseData => {
        // console.log('responseData:', responseData)
        const tenantText = responseData.filter(function(el) {
          return el.tenant === mediaName
        })
        // console.log('tenantText:', tenantText)
        this.setState({ tenantText: tenantText[0] })
      })

    /* GET SHEET DATA VIA CLOUD STORAGE */
    const dataURL =
      'https://storage.googleapis.com/ta-interaktiv/mitarbeiter-der-redaktion/2022-' +
      mediaName +
      '.json'
    // console.log('dataURL:', dataURL)
    fetch(dataURL)
      .then(response => {
        if (response.ok) {
          return response.json()
        } else {
          throw new Error('JSON Data was not OK')
        }
      })
      .then(responseData => {
        // console.log('responseData:', responseData)
        /* GROUP DATA BY REDAKTION */
        const groupedData = nest()
          .key(function(d) {
            return d.ressort
          })
          .entries(responseData.data)
        // console.log('groupedData:', groupedData)

        this.setState(
          {
            peopleData: responseData,
            publicationDate: this.extractPublicationDate(responseData),
            groupedData: groupedData
          },
          function() {
            const output = this.state.groupedData.map(
              function(bereich, index) {
                return (
                  <div className='ui vertical segment'>
                    <div className='ui container'>
                      <SectionTitle mediaName={mediaName}>
                        {bereich.key}
                      </SectionTitle>
                      <div className='ui centered grid'>
                        {bereich.values.map((person, i) => (
                          <Person
                            unityID={person.unityID}
                            name={person.names}
                            customImage={person.customImage}
                            unityImage={person.unityImage}
                            function={person.title}
                            hasBiography={person.hasBiography}
                            hasArticles={person.hasArticles}
                            mediaName={mediaName}
                          />
                        ))}
                      </div>
                    </div>

                    {index === 0 && (
                      <div className='ui text container'>
                        <div className='ui icon hover-hint message'>
                          <i className='hand pointer outline icon' />
                          <div className='content'>
                            <p
                              dangerouslySetInnerHTML={{
                                __html:
                                  this.state.tenantText.hint !== undefined
                                    ? this.state.tenantText.hint
                                    : 'Tippen Sie auf die Bilder, um mehr über die Personen zu erfahren.'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )
              }.bind(this)
            )
            this.setState({ output: output })
          }
        )
      })
  }

  render() {
    return (
      <div className='index'>
        <Masthead articleId={ARTICLE_ID} />

        <ThemeSwitch />

        <header className='ui vertical  very fitted  segment'>
          <div className='ui container'>
            <div className='ui header'>
              <h1
                dangerouslySetInnerHTML={{
                  __html:
                    this.state.tenantText.title !== undefined
                      ? this.state.tenantText.title
                      : 'Die Köpfe hinter dieser Zeitung'
                }}
              />
              <h2
                dangerouslySetInnerHTML={{
                  __html:
                    this.state.tenantText.lead !== undefined
                      ? this.state.tenantText.lead
                      : 'Die Redaktion stellt sich vor.'
                }}
              />
            </div>
          </div>
        </header>

        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='ta metainfo'>
              <div className='ta updated'>
                Aktualisiert am {this.state.publicationDate}
              </div>
            </div>
            <div
              className='ta text'
              dangerouslySetInnerHTML={{
                __html:
                  this.state.tenantText.introduction !== undefined
                    ? this.state.tenantText.introduction
                    : 'Für unsere Recherchen sind tagtäglich viele Redaktorinnen und Redaktoren im Einsatz. Die Redaktion ist so vielfältig wie die Themen, über die sie berichtet. Von Journalisten und Journalistinnen, die in Ihrer Region jede Ecke kennen, über Onlinespezialisten, Wirtschafts- und Kulturexpertinnen oder Sport-Spezialisten. Alle diese Kolleginnen und Kollegen geben unserer Zeitung ein Gesicht.'
              }}
            />
          </div>
        </div>

        {this.state.output}

        <Footer
          inverted
          articleId={ARTICLE_ID}
          hidePortfolioTeaser
          hideTop
          credits={[
            ['Umsetzung', 'Mathias Lutz'],
            ['Projektleitung', 'Claudia Bianchi']
          ]}
        />
      </div>
    )
  }
}
